// Pure nav derivations — no JSX, no StyleX — so they run under `bun test`.
import type { NavSection } from './nav-tree';

/** Case-insensitive nav filter: keeps whole sections whose title matches ("HQ" →
 *  every HQ page), leaves whose label matches, groups whose own label matches
 *  (whole group), or groups with matching children (those children only). Empty
 *  sections drop out. Titles arrive already translated, so a German title matches
 *  a German query. */
export function filterSections(sections: NavSection[], query: string): NavSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  const hit = (label: string) => label.toLowerCase().includes(q);
  return sections
    .map((sec) =>
      hit(sec.title)
        ? sec
        : {
            ...sec,
            items: sec.items.flatMap((node): NavSection['items'] => {
              if (node.kind === 'leaf') return hit(node.label) ? [node] : [];
              if (hit(node.label)) return [node];
              const children = node.children.filter((c) => hit(c.label));
              return children.length ? [{ ...node, children }] : [];
            }),
          },
    )
    .filter((sec) => sec.items.length > 0);
}

/** A leaf matches its own route, its sub-pages (`/assets` → `/assets/record`)
 *  and query variants (`/assets` → `/assets?state=stock`) — longest match wins,
 *  so a query-keyed sibling (Stockroom) beats its base route (Register). */
function matches(key: string, activeKey: string): boolean {
  return key === activeKey || activeKey.startsWith(`${key}/`) || activeKey.startsWith(`${key}?`);
}

/** Every navigable key in a section: its leaves and every group's children. */
export function sectionKeys(sec: NavSection): string[] {
  return sec.items.flatMap((i) => (i.kind === 'leaf' ? [i.key] : i.children.map((c) => c.key)));
}

/** Longest-match wins: `/assets` must NOT light up while `/assets/labels` has
 *  its own nav entry (both matched by prefix → double highlight otherwise). */
export function bestMatchKey(sections: NavSection[], activeKey: string): string | null {
  let best: string | null = null;
  for (const key of sections.flatMap(sectionKeys)) {
    if (matches(key, activeKey) && (best === null || key.length > best.length)) best = key;
  }
  return best;
}

/** The title of the section holding the current page (Audit Log → "Administration"), or null. */
export function activeSectionTitle(sections: NavSection[], activeKey: string): string | null {
  const leaf = bestMatchKey(sections, activeKey);
  if (!leaf) return null;
  return sections.find((s) => sectionKeys(s).includes(leaf))?.title ?? null;
}
